"use client";

import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  index: string;
  value: number;
  suffix: string;
  label: string;
  description: string;
};
const metrics: Metric[] = [{
  index: "01",
  value: 28.5,
  suffix: "%",
  label: "REVENUE GROWTH",
  description: "Average increase in RevPAR achieved for our partner hotels through strategic OTA positioning and dynamic pricing."
}, {
  index: "02",
  value: 42,
  suffix: "%",
  label: "DIRECT BOOKINGS",
  description: "Average shift from high-commission channels to direct website bookings after implementing our conversion strategies."
}, {
  index: "03",
  value: 150,
  suffix: "+",
  label: "HOTELS OPTIMIZED",
  description: "Properties across diverse markets successfully transformed through our data-driven revenue management frameworks."
}, {
  index: "04",
  value: 12,
  suffix: "",
  label: "YEARS EXPERTISE",
  description: "Over a decade of deep-dive experience in hospitality technology, revenue auditing, and market demand analysis."
}];
function CountUp({
  end,
  suffix,
  start,
  decimals = 0
}: {
  end: number;
  suffix: string;
  start: boolean;
  decimals?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1800;
    const startTime = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(end * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, end]);
  const formatted = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString("en-US");
  return <span>
      {formatted}
      {suffix}
      <span className="text-secondary">.</span>
    </span>;
}
export default function Performanceresults() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  return <section data-section-id="4852" id="performanceresults" className="bg-background text-foreground py-24 md:py-32 px-4 sm:px-6 lg:px-12">
      <div className="max-w-[1400px] mx-auto" ref={ref}>
        {}
        <div className="flex items-center justify-between gap-4 pb-6">
          <span className="font-default text-[11px] sm:text-xs uppercase tracking-widest text-muted-foreground">
            02 / 08 — Impact Metrics
          </span>
          <span className="font-default text-[11px] sm:text-xs uppercase tracking-widest text-muted-foreground">
            Proven Results
          </span>
        </div>
        <div className="h-px w-full bg-border" />

        {}
        <div className="pt-16 md:pt-20 pb-16 md:pb-24 max-w-[700px]">
          <p className={`font-default italic text-2xl sm:text-3xl md:text-[32px] leading-snug text-foreground transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            Data-driven revenue strategies that turn hospitality challenges into consistent, scalable growth.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-16 gap-x-0 relative">
          {metrics.map((metric, i) => <div data-index={i} key={metric.index} className={`relative px-0 sm:px-6 lg:px-8 first:pl-0 last:pr-0 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: `${i * 120}ms`
        }}>
              {}
              {i > 0 && <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-px">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-muted-foreground">
                    <Plus className="w-2.5 h-2.5" strokeWidth={1.5} />
                  </div>
                  <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-border" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-muted-foreground">
                    <Plus className="w-2.5 h-2.5" strokeWidth={1.5} />
                  </div>
                </div>}

              {}
              <div className="font-default text-[11px] uppercase tracking-widest text-muted-foreground mb-6">
                {metric.index}
              </div>

              {}
              <div className="font-default font-light text-[64px] sm:text-[72px] lg:text-[88px] xl:text-[96px] leading-[0.95] text-foreground mb-6 tracking-tight">
                <CountUp end={metric.value} suffix={metric.suffix} start={isVisible} decimals={metric.value % 1 !== 0 ? 1 : 0} />
              </div>

              {}
              <div className="font-default text-xs uppercase tracking-widest text-foreground mb-5">
                {metric.label}
              </div>

              {}
              <div className="h-px w-12 bg-border mb-5" />

              {}
              <p className="font-default text-[13px] leading-relaxed text-muted-foreground max-w-[260px]">
                {metric.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}